import { useState, useEffect } from 'react'

type QueryParams = Partial<Record<string, string>>
type QueryParamsSetter<T> = (params: T) => void
type QueryParamsRemover<T> = (keysToRemove: (keyof T)[]) => void
export default function useQueryParams<T extends QueryParams>(
  defaultParams: Partial<T>
): {
  queryParams: Partial<T>
  setQueryParams: QueryParamsSetter<Partial<T>>
  removeQueryParams: QueryParamsRemover<QueryParams>
} {
  const [queryParams, setParams] = useState<Partial<T>>(defaultParams)

  const handleSetQueryParams = (params: Partial<T>) => {
    setParams(currentParams => ({ ...currentParams, ...params }))

    const url = new URL(window.location.href)
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value)
    })
    window.history.replaceState({}, '', url)
  }

  const handleRemoveQueryParams = (keysToRemove: (keyof T)[]) => {
    const newQueryParams = { ...queryParams }
    const url = new URL(window.location.href)

    keysToRemove.forEach(key => {
      delete newQueryParams[key]
      url.searchParams.delete(key as string)
    })
    setParams(newQueryParams)
    window.history.replaceState(
      {},
      '',
      `${url.pathname}${url.search}${url.hash}`
    )
  }

  useEffect(() => {
    const url = new URL(window.location.href)
    const params: Record<string, string> = {}
    Array.from(url.searchParams.entries()).forEach(([key, value]) => {
      params[key] = value
    })
    setParams(params as T)
  }, [])

  return {
    queryParams,
    setQueryParams: handleSetQueryParams,
    removeQueryParams: handleRemoveQueryParams
  }
}
